import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const JennifersJourney = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imageFilter = useTransform(
    scrollYProgress,
    [0, 0.4, 1],
    ['grayscale(100%) brightness(0.5)', 'grayscale(40%) brightness(0.75)', 'grayscale(0%) brightness(1)']
  );

  return (
    <section ref={ref} className="relative bg-darker overflow-hidden border-t border-white/5">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[100vh]">
        {/* Parallax Image */}
        <div className="relative lg:sticky lg:top-0 lg:h-screen overflow-hidden">
          <motion.img
            src="https://media-manager-c.questera.ai/greta-media/8c84f85263efda659889de32f1f32e795be69e9a2504f254db44deecf03d5d2eaf8d8138b6c4ad97d7d004f4d5618742/images/aW1hZ2UvcG5n/0a8dbd1ab6196d088ee699a1d0e6492e.png"
            alt="Jennifer Gräser"
            style={{ filter: imageFilter }}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-darker lg:block hidden" />
          <div className="absolute inset-0 bg-gradient-to-t from-darker via-transparent to-transparent" />
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.08), transparent)',
              opacity: scrollYProgress,
            }}
          />
        </div>

        {/* Text Content */}
        <div className="relative z-10 flex flex-col justify-center px-10 md:px-16 py-28 space-y-10">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gold tracking-[0.3em] uppercase text-xs font-semibold"
          >
            Jennifers Reise
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-4xl md:text-5xl font-serif text-cream leading-tight"
          >
            Meine Reise: Vom Schmerz{' '}
            <span className="italic text-transparent bg-clip-text bg-gold-gradient block mt-2">
              zur Erfüllung.
            </span>
          </motion.h2>

          {[
            {
              delay: 0.2,
              text: `Vor einigen Jahren begegnete ich einer Verbindung, die mich bis ins Mark erschütterte. Die Intensität war unvergleichlich – und der Schmerz auch. Ablehnung. Alte Wunden, aufgerissen. Diese Art von Liebe, die sich gleichzeitig wie Schicksal und Zerstörung anfühlt.`,
            },
            {
              delay: 0.4,
              text: `Ich suchte die Antwort außerhalb von mir. Ich analysierte sein Verhalten, ich wartete, ich passte mich an. Und dabei verlor ich den Faden zurück zu mir selbst vollständig.`,
            },
            {
              delay: 0.6,
              highlight: true,
              text: `Der Wendepunkt kam, als ich verstand: Das war kein Fluch. Es war ein Ruf. Ein Ruf, die Teile von mir zu heilen, die sich nie sicher genug gefühlt hatten, einfach zu existieren.`,
            },
            {
              delay: 0.8,
              text: `Durch tiefe Identitätsarbeit baute ich mein Selbstbild von Grund auf neu auf. Ich löste die Muster auf, die mich von externer Bestätigung abhängig gemacht hatten. Und dann geschah etwas Außerordentliches:`,
            },
            {
              delay: 1.0,
              gold: true,
              text: `Die Abhängigkeit verschwand. Ich wurde magnetisch – nicht indem ich mich mehr anstrengte, sondern indem ich vollständiger ich selbst wurde. Liebe kam. Erfolg kam. Nicht als Belohnungen, sondern als natürliche Spiegelung dessen, wer ich geworden war.`,
            },
          ].map((block, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: block.delay, duration: 0.8 }}
              className={`font-light leading-relaxed text-lg ${
                block.gold
                  ? 'text-gold font-serif italic text-xl'
                  : block.highlight
                  ? 'text-cream font-serif italic text-xl'
                  : 'text-cream/65'
              }`}
            >
              {block.text}
            </motion.p>
          ))}

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.2 }}
            className="pt-6 border-t border-white/10"
          >
            <p className="text-cream font-semibold tracking-widest uppercase text-sm">Jennifer Gräser</p>
            <p className="text-gold text-xs uppercase tracking-widest mt-1">Gründerin · Coaching Female</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default JennifersJourney;